import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { useToast } from '../components/ui/use-toast';
import { Mountain, Clock, Users } from 'lucide-react';
import { competitionRoundsService } from '../services/competition-rounds';
import { competitionsService } from '../services/competitions';
import { useAuth } from '../hooks/useAuth';
import { roundRefreshOptions, useAutoRefresh } from '../hooks/useAutoRefresh';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { createSerialQueue } from '../lib/serial-queue';
import type { CompetitionRound, RoundClimb, RouteHold, BoulderRoundResult } from '../types/competition-round';
import type { Competition, CompetitionCategory } from '../types/competition';
import type { LeadCardDraft } from '../components/competition/LeadCardEditor';
import { ParticipantBoulderBlocksTable } from '../components/competition/ParticipantBoulderBlocksTable';
import { ParticipantLeadRoutesTable } from '../components/competition/ParticipantLeadRoutesTable';
import { RouteHoldsDialog } from '../components/competition/route-holds/RouteHoldsDialog';
import { RoundStandings } from '../components/competition/RoundStandings';
import { RoundClimbPhotoButton } from '../components/competition/RoundClimbPhotoButton';
import { RoundCategoryTabs } from '../components/competition/RoundCategoryTabs';
import { RefreshGenerationContext, useRefreshGeneration } from '../components/competition/refresh-flash';
import { SwipePager } from '../components/ui/swipe-pager';
import { RoundClimbThumbnail } from '../components/competition/RoundClimbThumbnail';
import { RoundClock } from '../components/competition/RoundClock';
import { useRoundTimeUp } from '../components/competition/round-clock';
import { BoulderEditDialog } from '../components/competition/BoulderEditDialog';
import { WallBlocksList } from '../components/competition/judge-block/WallBlocksList';
import {
  judgePagePath,
  landingProblem,
  loadJudgeClass,
  wallBlocks,
  type WallBlock,
} from '../components/competition/judge-block/judge-blocks';
import { competitionCrumb } from '../components/competition/competition-crumb';
import { RoundHelperStrip } from '../components/competition/round-delegation/RoundHelperStrip';
import { useRoundDelegation } from '../components/competition/round-delegation/round-delegation-context';
import {
  availableRoundViews,
  ownEntry,
  resolveRoundView,
  staffRolesHere,
} from '../components/competition/round-views';

/**
 * Where the class last read is kept, so coming back to a round opens on it
 * rather than on whichever class happens to be first.
 *
 * Keyed on the competition's slug: the same stage is the thing somebody works
 * through in one afternoon, and a class id from another circuit would mean
 * nothing here anyway.
 *
 * There is deliberately **no "All" tab**. A round's classes each climb their own
 * numbered problems, so listing them together gives four rows called "Blocco 1"
 * and no way to tell them apart — a tab that cannot be read is worse than one
 * fewer tab.
 */
const CATEGORY_STORAGE_PREFIX = 'round-category:';

function loadStoredCategory(slug: string | undefined): string | null {
  if (!slug) return null;
  try {
    return sessionStorage.getItem(`${CATEGORY_STORAGE_PREFIX}${slug}`) || null;
  } catch {
    return null;
  }
}

function storeCategory(slug: string | undefined, categoryId: string) {
  if (!slug) return;
  try {
    sessionStorage.setItem(`${CATEGORY_STORAGE_PREFIX}${slug}`, categoryId);
  } catch {
    // A private window, or site data blocked. The tab still works, it just
    // opens on the first class next time.
  }
}

/**
 * One round, read one of four ways (see `RoundView`): the room's (its classes
 * and their blocks), the athlete's (their own blocks, marked inline), the
 * judge's (the blocks on the wall, each one the way to everybody who climbs it)
 * and the team manager's (one athlete's whole card — its own page, which this
 * one forwards to). Somebody with several jobs here picks one on the
 * competition page; the address says which (`?view=`), and the last one picked
 * is what a round opens on next.
 *
 * On a helper's phone (RoundHelperGate) it is only ever the athlete's view, of
 * the card of the athlete who shared it: nobody here is signed in as far as
 * this round is concerned, so the views, the roles and the entry come from the
 * delegation, never from whoever the phone's own session belongs to.
 */
export default function CompetitionRoundDetail() {
  const { slug, roundId } = useParams<{ slug: string; roundId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const helping = useRoundDelegation();

  const [competition, setCompetition] = useState<Competition | null>(null);
  const [round, setRound] = useState<CompetitionRound | null>(null);
  const [categories, setCategories] = useState<CompetitionCategory[]>([]);
  const [loading, setLoading] = useState(true);

  // Which category tab is being read. Kept in the URL — the link anybody
  // shares should open on the class they were looking at.
  const [searchParams, setSearchParams] = useSearchParams();
  // What the URL asks for. Resolved against the round's own classes further down
  // (`selectedCategoryTab`), because a stale link or a remembered id from
  // another competition must not leave the page filtering on nothing.
  const askedCategory = searchParams.get('category');
  const askedView = searchParams.get('view');

  /** The athlete's own card on one route, while it is being saved. */
  const [savingLeadCard, setSavingLeadCard] = useState<string | null>(null);

  /**
   * The route whose holds the route setter is circling, and whether that drawing
   * is being saved. On a lead round the circles *are* the route's holds count, so
   * this is also how a route becomes climbable (see RouteHoldsDialog).
   */
  const [drawingRouteId, setDrawingRouteId] = useState<string | null>(null);
  const [savingHolds, setSavingHolds] = useState(false);

  // Boulder edit dialog state
  const [boulderEditDialogOpen, setBoulderEditDialogOpen] = useState(false);
  const [editingRoundClimbId, setEditingRoundClimbId] = useState<string | null>(null);

  const roles = helping ? [] : staffRolesHere(user?.staff, slug);
  // Whoever describes the wall: its photos, and a route's holds circled on them.
  const describesWall = roles.some((role) => ['admin', 'editor', 'routesetter'].includes(role));
  // Anybody who works this competition, whatever their role. What separates the
  // people who run a round from the people who climb in it.
  const isStaff = roles.length > 0;

  const views = useMemo(
    () => availableRoundViews({ staff: user?.staff, competition, categoryIds: round?.category_ids }),
    [user?.staff, competition, round?.category_ids]
  );
  const view = helping ? 'athlete' : resolveRoundView(askedView, views, slug, user?.id);


  /**
   * The viewer's own entry: whose blocks the athlete's view lists, and whose
   * card it writes on. A parent scoring for a child uses the team manager's
   * view, where the athlete is picked by name.
   */
  const me = helping ? helping.delegation.grantor_entry : ownEntry(competition);
  const scoringAs = view === 'athlete' ? me : null;

  /**
   * The round's own classes, resolved to names — what the category tabs list.
   */
  const roundCategories = useMemo(
    () => categories.filter(c => round?.category_ids.includes(c.id)),
    [categories, round?.category_ids]
  );

  /**
   * The class being read. Always one of the round's own — there is no "all".
   *
   * In order: what the URL asks for, then the one this reader last picked on this
   * stage, then the first class with anything on the wall. Each is checked
   * against the round's classes, so a shared link to a class this round does not
   * run, or a remembered id from another competition, falls back instead of
   * filtering on nothing.
   *
   * "With anything on the wall" rather than plainly the first, because a round
   * is often set one class at a time: opening on a class whose problems are not
   * numbered yet shows an empty round to somebody who was looking at the one
   * being climbed.
   */
  const selectedCategoryTab = useMemo(() => {
    const runs = (id: string | null) => !!id && roundCategories.some(c => c.id === id);
    if (runs(askedCategory)) return askedCategory as string;

    const remembered = loadStoredCategory(slug);
    if (runs(remembered)) return remembered as string;

    const withProblems = roundCategories.find(category =>
      round?.round_climbs?.some(rc => rc.competition_category_id === category.id)
    );
    return withProblems?.id ?? roundCategories[0]?.id ?? null;
  }, [askedCategory, roundCategories, round?.round_climbs, slug]);

  // Runs each climb's own attempts one after another (see createSerialQueue),
  // and the "do I already have a result on this climb" answer they act on.
  const runSerially = useRef(createSerialQueue()).current;
  const myResultsCacheRef = useRef<Map<string, BoulderRoundResult>>(new Map());

  const fetchData = useCallback(async () => {
    if (!slug || !roundId) return;

    try {
      const [competitionData, roundData, categoriesData] = await Promise.all([
        competitionsService.getCompetitionBySlug(slug),
        competitionRoundsService.getRound(slug, roundId),
        competitionsService.getCategories(slug),
      ]);

      setCompetition(competitionData);
      setRound(roundData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to fetch round:', error);
      toast({
        title: t('errors.loadFailed'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [slug, roundId, t, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * The round again at the competition's pace (roundRefreshOptions), without the
   * skeleton: its state (the panel starts it, stops it, closes it — and a closed
   * round is its standings), the blocks and their photos, and the athlete's own
   * card, which a judge writes on the judge-only blocks. The competition too, which says whether the desk has
   * confirmed the viewer's entry yet.
   *
   * The round is dropped if one of the athlete's own marks went out while it was
   * being read (see SerialQueue#quietSince): an answer from before the tap would
   * take it back. Otherwise the cards it carries replace the ones the taps were
   * reasoning from.
   */
  // Bumped with each refresh's round, so what somebody else changed lights up:
  // the round's state, the athlete's cards.
  const [generation, bumpGeneration] = useRefreshGeneration();

  const refresh = useCallback(async () => {
    if (!slug || !roundId) return;
    const mark = runSerially.mark();
    const [competitionData, roundData] = await Promise.all([
      competitionsService.getCompetitionBySlug(slug),
      competitionRoundsService.getRound(slug, roundId),
    ]);
    setCompetition(competitionData);
    if (!runSerially.quietSince(mark)) return;
    myResultsCacheRef.current.clear();
    setRound(roundData);
    bumpGeneration();
  }, [slug, roundId, runSerially, bumpGeneration]);

  // At the competition's pace. Nothing moves on a closed round: it is its
  // standings, final.
  useAutoRefresh(refresh, roundRefreshOptions(round, !loading));

  /** Folds a fresh result into both round state and the cache actions read. */
  const rememberMyResult = (roundClimbId: string, result: BoulderRoundResult) => {
    myResultsCacheRef.current.set(roundClimbId, result);
    setRound(current =>
      current
        ? {
            ...current,
            round_climbs: current.round_climbs?.map(rc =>
              rc.id === roundClimbId ? { ...rc, my_result: result } : rc
            ),
          }
        : current
    );
  };

  /**
   * Inline boulder action for the athlete's own card (using attempts API). Each
   * climb's own attempts are recorded in the order they were tapped, however
   * fast that happens — see createSerialQueue. The "do I have a result yet"
   * question is answered from myResultsCacheRef, updated the instant each
   * response lands, not from component state a queued call might still see
   * as it was before the last one finished.
   */
  const handleUserBoulderAction = (roundClimb: RoundClimb, action: 'zone' | 'top' | 'close') =>
    runSerially(roundClimb.id, async () => {
      if (!slug || !round) return;

      let myResult = myResultsCacheRef.current.get(roundClimb.id)
        ?? (roundClimb.my_result as BoulderRoundResult | null)
        ?? undefined;

      try {
        // If no result exists, create one first (with 0 attempts), on the
        // viewer's own entry.
        if (!myResult) {
          myResult = await competitionRoundsService.createBoulderResult(
            slug, round.id, roundClimb.id,
            { attempts_count: 0, participant_id: scoringAs?.participant_id }
          ) as BoulderRoundResult;
          myResultsCacheRef.current.set(roundClimb.id, myResult);
        }

        const attemptType = action === 'close' ? 'attempt' : action;

        const updated = await competitionRoundsService.addBoulderAttempt(
          slug, round.id, roundClimb.id, myResult.id, attemptType
        ) as BoulderRoundResult;

        // The button that was pressed shows the check itself, and the
        // response already carries the new attempt — no need to refetch the
        // whole round for what it just told us.
        rememberMyResult(roundClimb.id, updated);
      } catch (error) {
        toast({
          title: t('errors.actionFailed'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
        // Let the button know, so it drops back to idle rather than claiming
        // the attempt was recorded.
        throw error;
      }
    });

  // In the same queue as the taps that put the marks there.
  const handleUserRemoveAttempt = (roundClimb: RoundClimb, attemptId: string) =>
    runSerially(roundClimb.id, async () => {
      if (!slug || !round) return;

      const myResult = myResultsCacheRef.current.get(roundClimb.id)
        ?? (roundClimb.my_result as BoulderRoundResult | null);
      if (!myResult) return;

      try {
        const updated = await competitionRoundsService.removeBoulderAttempt(
          slug, round.id, roundClimb.id, myResult.id, attemptId
        ) as BoulderRoundResult;
        rememberMyResult(roundClimb.id, updated);
      } catch (error) {
        toast({
          title: t('errors.actionFailed'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      }
    });

  const handleOpenBoulderEditDialog = (roundClimbId: string) => {
    setEditingRoundClimbId(roundClimbId);
    setBoulderEditDialogOpen(true);
  };

  /**
   * The athlete's own card on one route, from their table: created the first
   * time, overwritten afterwards — one go per route means there is nothing to add
   * to and no order to keep.
   *
   * The round climb in hand is updated with what comes back rather than the whole
   * page being refetched: the row redraws, and the rest of the round is untouched.
   */
  const handleSaveMyLeadCard = (roundClimb: RoundClimb, draft: LeadCardDraft) =>
    // Queued like a boulder tap, which is also what keeps the minute's refresh
    // from landing on top of it.
    runSerially(roundClimb.id, async () => {
      if (!slug || !round) return;

      setSavingLeadCard(roundClimb.id);
      try {
        const payload = {
          holds_count: draft.holds,
          time_seconds: draft.timeSeconds ?? undefined,
          climbed_as: draft.style ?? undefined,
        };
        const existing = roundClimb.my_result;

        const saved = existing
          ? await competitionRoundsService.updateResult(
              slug,
              round.id,
              roundClimb.id,
              existing.id,
              payload
            )
          : await competitionRoundsService.createLeadResult(slug, round.id, roundClimb.id, {
              ...payload,
              participant_id: scoringAs?.participant_id,
            });

        setRound((current) =>
          current
            ? {
                ...current,
                round_climbs: current.round_climbs?.map((rc) =>
                  rc.id === roundClimb.id ? { ...rc, my_result: saved } : rc
                ),
              }
            : current
        );
      } catch (error) {
        toast({
          title: t('errors.actionFailed'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      } finally {
        setSavingLeadCard(null);
      }
    });

  /**
   * Saves a route's drawing. The circles go up whole — this is what the route's
   * holds should end up being — and the server counts them into `holds_count`,
   * which is why nothing here sends a number.
   *
   * The one refusal worth expecting is rubbing out a circle somebody has already
   * climbed to: the server says so, and the drawing stays as it was.
   */
  const handleSaveRouteHolds = async (holds: RouteHold[]) => {
    if (!slug || !round || !drawingRouteId) return;

    setSavingHolds(true);
    try {
      replaceRoundClimb(
        await competitionRoundsService.updateRoundClimb(slug, round.id, drawingRouteId, { holds })
      );
      setDrawingRouteId(null);
    } catch (error) {
      toast({
        title: t('leadRounds.draw.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSavingHolds(false);
    }
  };

  const handleTimeUp = useRoundTimeUp(setRound);

  // Starting and closing a round is done from the gym panel, not from here:
  // this page is what the room looks at, and the clock is not something to
  // press by mistake from a phone at the wall.

  /**
   * One round climb as the server just sent it back — after a photo was hung
   * on it — put in place of the one the page had. The result the viewer may
   * have on it is kept: the PATCH answers as staff, who have none.
   */
  const replaceRoundClimb = (updated: RoundClimb) => {
    setRound(current =>
      current
        ? {
            ...current,
            round_climbs: current.round_climbs?.map(rc =>
              rc.id === updated.id ? { ...updated, my_result: rc.my_result } : rc
            ),
          }
        : current
    );
  };

  /**
   * The room reads the round one class at a time, from the tabs: without them it
   * got every class at once, which is four rows called "Blocco 1" and no way to
   * tell which is which. The athlete's view is fixed to their own class, and the
   * judge's lists the blocks on the wall, which the classes share.
   */
  const showCategoryTabs = view === 'public' && roundCategories.length > 1;

  /**
   * The athlete booked the **other** turn of a class split over several (a
   * bookable round, CompetitionRound#bookable). Their card is that round's: the
   * server refuses one here (`not_in_this_round`), so rather than offering their
   * class's blocks the page says which turn is theirs.
   */
  const bookedElsewhere =
    view === 'athlete' &&
    !!round?.bookable &&
    !!scoringAs?.competition_round_id &&
    scoringAs.competition_round_id !== round.id;

  const selectCategoryTab = (categoryId: string) => {
    storeCategory(slug, categoryId);
    const next = new URLSearchParams(searchParams);
    next.set('category', categoryId);
    setSearchParams(next);
  };

  /**
   * Put the class that is actually being read into the URL, so the link somebody
   * shares opens on what they were looking at — and so a reload does not fall
   * back to a different class than the one on screen.
   *
   * `replace`, because landing on a round and being shown a class is not a step
   * anybody took: it should not need a second press of Back to leave.
   */
  useEffect(() => {
    if (!showCategoryTabs || !selectedCategoryTab) return;
    if (askedCategory === selectedCategoryTab) return;

    const next = new URLSearchParams(searchParams);
    next.set('category', selectedCategoryTab);
    setSearchParams(next, { replace: true });
  }, [showCategoryTabs, selectedCategoryTab, askedCategory, searchParams, setSearchParams]);

  /**
   * What this viewer gets to see of the round's problems. The athlete has their
   * own class fixed for them: only their class's problems and the shared ones,
   * so a round with several classes running at once does not show them blocks
   * that are not theirs.
   *
   * The room reads it through the category tab — a shared problem (no class of
   * its own) shows under every tab, same as it climbs for every class. Asked per
   * class rather than only for the open one, because the page beside it is
   * drawn too while it is being swiped in.
   */
  const climbsFor = (categoryId: string | null) => (round?.round_climbs ?? []).filter(rc => {
    if (view === 'athlete') {
      const mine = scoringAs?.competition_category_id ?? null;
      return rc.competition_category_id === null || rc.competition_category_id === mine;
    }

    // One class at a time, always: a round with no classes at all (nothing to
    // pick) shows its problems whole.
    if (!categoryId) return true;
    return rc.competition_category_id === null || rc.competition_category_id === categoryId;
  });

  /**
   * The wall is described from the room's view, which is the one that shows it:
   * the photo, and on a lead round the holds circled on it — by whoever runs the
   * round or set it, never by a judge, who scores it. The photo only while the
   * stage is still being got ready (announced, or taking entries): once it is
   * under way the pictures are what the room has been reading.
   */
  const canSetPhotos =
    view === 'public' &&
    describesWall &&
    (competition?.status === 'scheduled' || competition?.status === 'open');
  const canDrawHolds = view === 'public' && describesWall && round?.round_type === 'Lead';

  /** The route the drawing dialog is open on, as the page last had it. */
  const drawingRoute = drawingRouteId
    ? (round?.round_climbs?.find((rc) => rc.id === drawingRouteId) ?? null)
    : null;

  /**
   * A closed round **is** its standings: a table per class, and nothing else on
   * the page. Not a moment earlier — while the cards are still being put straight
   * a published order would be wrong in front of the people it is about — and
   * nothing after it either: the problems are shown while there is something to
   * do with them, and on a closed round there is not (see RoundStandings).
   */
  const showStandings = !!round?.closed;

  /** The judge picked a block on the wall: its page, on the class last scored. */
  const openWallBlock = (block: WallBlock) => {
    if (!round || !slug) return;
    const preferred = loadJudgeClass(slug, round.id);
    const roundClimb = landingProblem(block, preferred, roundCategoryIds);
    if (!roundClimb) return;
    navigate(judgePagePath(slug, round, roundClimb, roundClimb.competition_category_id ?? preferred));
  };

  useBreadcrumb(() => {
    const toStage = () => navigate(`/competitions/${slug}`);
    // The stage's logo stands first, where DivePeaks' would; without one, its
    // name does.
    const stage = competitionCrumb(competition, toStage);
    return {
      onBack: toStage,
      hideHomeLogo: stage.hideHomeLogo,
      segments: competition && round
        ? [
            ...(stage.segments.length > 0
              ? stage.segments
              : [{ label: competition.name, onClick: toStage, muted: true }]),
            { label: round.name },
          ]
        : [],
      loading,
    };
  }, [competition, round?.name, loading, slug]);

  /** The classes' tabs, in order: the pages the blocks are swiped through. */
  const roundCategoryIds = useMemo(() => roundCategories.map((c) => c.id), [roundCategories]);

  if (!slug || !roundId) {
    return <Navigate to="/not-found" replace />;
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-16 w-16 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!round || !competition) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardContent className="pt-6 text-center">
            <h1 className="text-2xl font-bold text-muted-foreground">{t('competitionRounds.notFound', { defaultValue: 'Round not found' })}</h1>
          </CardContent>
        </Card>
      </div>
    );
  }

  // The team manager's view is one athlete at a time, on a page of its own: the
  // last one picked in this round, or the first by name.
  if (view === 'team' && !showStandings) {
    return <Navigate to={`/competitions/${slug}/rounds/${roundId}/athletes`} replace />;
  }

  /**
   * One class's page of the round as the room reads it: the blocks, with their
   * photos. Read-only — whoever has marks to write has a view of their own.
   */
  const renderPublicBlocks = (categoryId: string | null) => {
    const roundClimbs = climbsFor(categoryId);

    if (roundClimbs.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <Mountain className="h-12 w-12 mx-auto mb-4 opacity-50" />
          {/* Which emptiness: a round with nothing on the wall at all, or
              the class being read having nothing of its own yet — rounds
              are often set one class at a time. */}
          <p>
            {t(
              round.climbs_count > 0 && categoryId
                ? 'competitionRounds.noClimbsForCategory'
                : 'competitionRounds.noClimbs'
            )}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {roundClimbs.map((roundClimb, index) => (
          <div key={roundClimb.id} className="p-4 border rounded-lg" data-testid="public-block">
            <div className="flex items-center gap-4">
              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-sm font-semibold">
                {index + 1}
              </div>

              {/* The problem's picture. One set the night before has none
                  until somebody takes it — the camera beside the name. */}
              {roundClimb.image_small_url ? (
                <RoundClimbThumbnail
                  roundClimb={roundClimb}
                  className="h-16 w-16"
                  // On a lead round the route setter's tap on the photo is how
                  // the route's holds get circled, not how the picture is
                  // enlarged.
                  onOpen={canDrawHolds ? () => setDrawingRouteId(roundClimb.id) : undefined}
                />
              ) : (
                <div className="h-16 w-16 rounded-lg bg-muted flex items-center justify-center">
                  <Mountain className="h-8 w-8 text-muted-foreground" />
                </div>
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 min-w-0">
                  <h3 className="font-semibold truncate">
                    {roundClimb.display_name || `Problem ${index + 1}`}
                  </h3>
                  {canSetPhotos && (
                    <RoundClimbPhotoButton
                      competitionSlug={slug}
                      roundId={roundId}
                      roundClimb={roundClimb}
                      onUploaded={replaceRoundClimb}
                    />
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {roundClimb.climb?.difficulty && (
                    <Badge variant="outline">{roundClimb.climb.difficulty}</Badge>
                  )}
                  {roundClimb.requires_judge && (
                    <Badge variant="outline" className="text-blue-600 border-blue-600">
                      <Users className="h-3 w-3 mr-1" />
                      {t('competitionRounds.requiresJudge')}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  /**
   * Whether the athlete may still mark their own card: while the round runs, and
   * through the corrections after the clock, to put right a top they forgot.
   * Putting the round under review from the panel ends it (Lifecycle::Round):
   * the card is still read, with a notice saying who checks it now.
   */
  const scoringOpen = helping ? !!round.can_write : round.running || round.correcting;

  /** The athlete's own blocks, each one marked inline where it is tapped. */
  const renderAthleteBlocks = () => {
    if (bookedElsewhere) {
      return (
        <div className="py-6 text-center" data-testid="booked-elsewhere">
          <Clock className="mx-auto mb-3 h-10 w-10 text-muted-foreground opacity-60" aria-hidden />
          <p className="text-sm">
            {t('competitionRounds.bookedElsewhere', { turn: scoringAs?.competition_round_name ?? '' })}
          </p>
          {/* Not from a helper's phone: its link is for this round only. */}
          {!helping && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => navigate(`/competitions/${slug}/rounds/${scoringAs?.competition_round_id}?view=athlete`)}
              data-testid="go-to-booked-turn"
            >
              {t('competitionRounds.goToBookedTurn', { turn: scoringAs?.competition_round_name ?? '' })}
            </Button>
          )}
        </div>
      );
    }

    const roundClimbs = climbsFor(null);
    return round.round_type === 'Lead' ? (
      <ParticipantLeadRoutesTable
        roundClimbs={roundClimbs}
        scoringOpen={scoringOpen}
        saving={savingLeadCard}
        onSave={handleSaveMyLeadCard}
      />
    ) : (
      <ParticipantBoulderBlocksTable
        roundClimbs={roundClimbs}
        scoringOpen={scoringOpen}
        onRecordAttempt={(rc) => handleUserBoulderAction(rc, 'close')}
        onRecordZone={(rc) => handleUserBoulderAction(rc, 'zone')}
        onRecordTop={(rc) => handleUserBoulderAction(rc, 'top')}
        onEdit={handleOpenBoulderEditDialog}
      />
    );
  };

  const categoryTabs = showCategoryTabs && !!selectedCategoryTab && !showStandings;
  // An entry the desk has not confirmed yet climbs nothing here, and should
  // know why rather than find no buttons.
  const awaitingConfirmation = view === 'public' && !!me && !me.confirmed;

  return (
    <RefreshGenerationContext.Provider value={generation}>
      <div>
        {/* The round's state and clock: a strip under the navbar, with a
            hairline that empties while the round runs (RoundClock). "Draft"
            only to the people who would start it. */}
        <RoundClock round={round} onTimeUp={handleTimeUp} onStartReached={fetchData} showDraft={isStaff} />

        <RoundHelperStrip canWrite={scoringOpen} className="mt-3" />

        {/* Being checked: the athletes and their squads read their cards, the
            judges and the organisers write them. */}
        {round.under_review && !isStaff && (
          <div
            className="mt-3 rounded-lg border border-dashed p-3 text-sm text-muted-foreground"
            data-testid="round-under-review-notice"
          >
            {t('competitionRounds.underReviewNotice')}
          </div>
        )}

        {/* The class being read, pinned under the navbar (its height, strip
            included). Nothing else — the state and the clock are in the
            navbar's strip, and how long it was set to run and how many problems it
            holds are the setup's business, read in the panel. */}
        {categoryTabs && (
          <div
            className="sticky top-[var(--navbar-height,65px)] z-30 -mx-4 -mt-3 mb-4 border-b bg-background px-4 py-2 sm:-mx-6 sm:px-6"
            data-testid="round-bar"
          >
            {/* One tab per class the round runs, and no "all": each class climbs
                its own numbered problems, so together they are four rows called
                "Blocco 1". What the reader picks lives in the URL and is
                remembered, so the round page opens back on it. */}
            <RoundCategoryTabs
              categories={roundCategories}
              selectedId={selectedCategoryTab}
              onSelect={selectCategoryTab}
            />
          </div>
        )}

        <div className={view === 'public' ? 'max-w-4xl mx-auto space-y-6' : 'w-full space-y-6'}>
          {/* The sheet at the end of the afternoon, once the round is closed: one
              table per class, with the position beside the name and the squad. */}
          {showStandings && (
            <Card>
              <CardHeader>
                <CardTitle>{t('competitionRounds.standings.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <RoundStandings
                  competitionSlug={slug}
                  roundId={roundId}
                  roundType={round.round_type}
                />
              </CardContent>
            </Card>
          )}

          {/* The problems, while there is still something to do with them. A
              closed round is its standings and nothing else. No card around
              them — the page is the list, and a box inside the page only took
              width off a phone. */}
          {!showStandings && (
            <div data-testid="round-blocks" data-view={view}>
              {awaitingConfirmation && (
                <p
                  className="mb-4 rounded-lg border border-dashed p-3 text-sm text-muted-foreground"
                  data-testid="awaiting-confirmation"
                >
                  {t('competitionRounds.awaitingConfirmation')}
                </p>
              )}

              {view === 'judge' ? (
                <WallBlocksList
                  blocks={wallBlocks(round.round_climbs ?? [])}
                  categories={roundCategories}
                  onSelect={openWallBlock}
                />
              ) : view === 'athlete' ? (
                renderAthleteBlocks()
              ) : categoryTabs && selectedCategoryTab ? (
                // One page per class, swiped through beside the tabs that name
                // them.
                <SwipePager
                  ids={roundCategoryIds}
                  activeId={selectedCategoryTab}
                  onChange={selectCategoryTab}
                  renderPage={renderPublicBlocks}
                  className="-mx-4 sm:-mx-6"
                  pageClassName="px-4 sm:px-6"
                />
              ) : (
                renderPublicBlocks(selectedCategoryTab)
              )}
            </div>
          )}

          {/* Where a lead route's holds are circled — and so where its holds count
              comes from. The route setter's, on a lead round. */}
          {drawingRoute?.image_url && (
            <RouteHoldsDialog
              open
              onOpenChange={(next) => !next && setDrawingRouteId(null)}
              name={drawingRoute.display_name ?? ''}
              imageUrl={drawingRoute.image_url}
              holds={drawingRoute.holds ?? []}
              saving={savingHolds}
              onSave={handleSaveRouteHolds}
            />
          )}

          {/* Boulder Edit Dialog for the athlete's own card */}
          {(() => {
            const editingClimb = editingRoundClimbId
              ? round?.round_climbs?.find(rc => rc.id === editingRoundClimbId)
              : null;
            const editingResult = editingClimb?.my_result as BoulderRoundResult | null;
            return (
              <BoulderEditDialog
                open={boulderEditDialogOpen}
                onOpenChange={setBoulderEditDialogOpen}
                attempts={editingResult?.boulder_attempts || []}
                onRemoveAttempt={(attemptId) => {
                  if (editingClimb) handleUserRemoveAttempt(editingClimb, attemptId);
                }}
              />
            );
          })()}
        </div>
      </div>
    </RefreshGenerationContext.Provider>
  );
}
